import { useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PackagePlus, Pencil, ShoppingCart, Tags } from "lucide-react";

import { inventoryService, type InventoryItem, type ItemCategory } from "../../services/inventoryService";
import { apiErrorMessage } from "../../utils/apiError";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { ReasonPanel, errorLine } from "./InventoryParts";
import { ITEM_TYPES, day, itemConfig, labelFrom, money, movementLabel, needsReorder, reorderLines, stockStatus, stockValue, type OrderLine } from "./inventoryLogic";

function ItemForm({ item, categories, onClose, onDone }: { item?: InventoryItem; categories: ItemCategory[]; onClose: () => void; onDone: (item: InventoryItem) => void }) {
  const save = useMutation({
    mutationFn: (data: Partial<InventoryItem>) => (item ? inventoryService.updateInventoryItem(item.id, data) : inventoryService.createInventoryItem(data)),
    onSuccess: onDone,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim();
    const data: Partial<InventoryItem> = {
      name: text("name"),
      sku: text("sku").toUpperCase(),
      category: Number(text("category")),
      item_type: text("item_type"),
      unit_cost: text("unit_cost"),
      unit_of_measure: text("unit_of_measure") || "pieces",
      storage_location: text("storage_location") || null,
      barcode: text("barcode") || null,
      minimum_stock: Number(text("minimum_stock") || 0),
      reorder_level: Number(text("reorder_level") || 0),
      maximum_stock: Number(text("maximum_stock") || 0),
      description: text("description"),
    };
    if (!item) Object.assign(data, { current_stock: Number(text("current_stock") || 0), is_active: true });
    save.mutate(data);
  };
  const value = (name: keyof InventoryItem) => (item?.[name] as string | number | null | undefined) ?? "";

  return (
    <FormModal
      id="stock-item"
      title={item ? `Edit ${item.name}` : "Add a stock item"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The item could not be saved.") : undefined}
      saveLabel={item ? "Save changes" : "Add the item"}
      note={item ? "The count changes only through adjustments and deliveries." : undefined}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The item" divided={false}>
        <Field label="Name" hint="e.g. Exercise book, 96 pages.">
          <input required name="name" maxLength={200} defaultValue={value("name")} className="input mt-1" />
        </Field>
        <Field label="Code (SKU)" hint="Your own short code; no two items share one.">
          <input required name="sku" maxLength={50} defaultValue={value("sku")} className="input mt-1 uppercase" placeholder="e.g. EXB-96" />
        </Field>
        <Field label="Category" hint={categories.length ? undefined : "Add a category first, with Categories."}>
          <select required name="category" defaultValue={value("category")} className="input mt-1">
            <option value="">Choose a category</option>
            {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
          </select>
        </Field>
        <Field label="Kind">
          <select name="item_type" defaultValue={value("item_type") || "stationery"} className="input mt-1">
            {ITEM_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Counted in" hint="pieces, boxes, reams, litres…">
          <input name="unit_of_measure" maxLength={20} defaultValue={value("unit_of_measure") || "pieces"} className="input mt-1" />
        </Field>
        <Field label="Unit cost (UGX)" hint="Fills in purchase order lines.">
          <input required type="number" name="unit_cost" min={0} step="any" defaultValue={value("unit_cost")} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Kept at" optional hint="e.g. Main store, shelf B.">
          <input name="storage_location" maxLength={200} defaultValue={value("storage_location")} className="input mt-1" />
        </Field>
        <Field label="Barcode" optional>
          <input name="barcode" maxLength={50} defaultValue={value("barcode")} className="input mt-1" />
        </Field>
        <Field label="Description" optional className="sm:col-span-2">
          <textarea name="description" rows={2} defaultValue={value("description")} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="Stock levels" description="Minimum ≤ reorder level ≤ maximum." columns={item ? 3 : 4}>
        {!item && (
          <Field label="In stock now" hint="Counted today.">
            <input type="number" name="current_stock" min={0} step={1} defaultValue={0} className="input mt-1 tabular-nums" />
          </Field>
        )}
        <Field label="Reorder at" hint="Shows under “Reorder now”.">
          <input required type="number" name="reorder_level" min={0} step={1} defaultValue={value("reorder_level") || 10} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Minimum" hint="Below this it is Low.">
          <input required type="number" name="minimum_stock" min={0} step={1} defaultValue={value("minimum_stock") || 0} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Maximum" hint="Orders top up to this.">
          <input required type="number" name="maximum_stock" min={0} step={1} defaultValue={value("maximum_stock") || 100} className="input mt-1 tabular-nums" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** What the stores hold, what to reorder, and every count that changed. */
export function StockPanel({ items, categories, isLoading, isError, onRetry, onChanged, onOpenCategories, onReorder }: {
  items?: InventoryItem[];
  categories: ItemCategory[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
  onOpenCategories: () => void;
  /** Starts a purchase order with these lines. */
  onReorder: (lines: OrderLine[]) => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<InventoryItem | "new" | null>(null);
  const [direction, setDirection] = useState<"out" | "in">("out");
  const [quantity, setQuantity] = useState("");
  const history = useQuery({ queryKey: ["inventory-movements", viewing], queryFn: () => inventoryService.getItemMovements(viewing!), enabled: viewing !== null });
  const adjust = useMutation({
    mutationFn: ({ id, change, reason }: { id: number; change: number; reason: string }) => inventoryService.adjustStock(id, change, reason),
    onSuccess: () => {
      setQuantity("");
      setNotice({ tone: "success", text: "The count is updated." });
      history.refetch();
      onChanged();
    },
  });
  const toggle = useMutation({
    mutationFn: (row: InventoryItem) => inventoryService.updateInventoryItem(row.id, { is_active: !row.is_active }),
    onSuccess: (row) => {
      setNotice({ tone: "success", text: row.is_active ? `${row.name} is stocked again.` : `${row.name} is no longer stocked; it stays in the history.` });
      onChanged();
    },
  });
  const toReorder = (items ?? []).filter(needsReorder);

  const columns: RegisterColumn<InventoryItem>[] = [
    {
      header: "Item",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.name}</span>
          <span className="block text-xs text-gray-500">{row.sku}</span>
        </span>
      ),
      exportValue: (row) => row.name,
      className: "min-w-[12rem]",
    },
    { header: "Code", cell: (row) => row.sku, exportValue: (row) => row.sku, exportOnly: true },
    { header: "Category", cell: (row) => row.category_name, exportValue: (row) => row.category_name, className: "text-gray-700" },
    { header: "In stock", cell: (row) => <span className={row.current_stock <= row.reorder_level && row.is_active ? "font-semibold text-red-700" : "font-medium"}>{row.current_stock} <span className="text-xs font-normal text-gray-500">{row.unit_of_measure}</span></span>, exportValue: (row) => row.current_stock, align: "right" },
    { header: "Reorder at", cell: (row) => row.reorder_level, exportValue: (row) => row.reorder_level, align: "right", className: "text-gray-600" },
    { header: "Unit cost", cell: (row) => money(row.unit_cost), exportValue: (row) => Number(row.unit_cost), align: "right" },
    { header: "Value", cell: (row) => money(stockValue(row)), exportValue: (row) => stockValue(row), align: "right" },
    { header: "Kept at", cell: (row) => row.storage_location || <span className="text-gray-300">—</span>, exportValue: (row) => row.storage_location ?? "", className: "text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...stockStatus(row)} />, exportValue: (row) => stockStatus(row).label },
  ];

  const item = viewing !== null ? items?.find((row) => row.id === viewing) : undefined;
  const change = quantity && Number(quantity) > 0 ? (direction === "out" ? -1 : 1) * Math.round(Number(quantity)) : 0;
  const after = item ? item.current_stock + change : 0;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {toReorder.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span>{toReorder.length} {toReorder.length === 1 ? "item is" : "items are"} at or below the reorder level.</span>
          <button type="button" onClick={() => onReorder(reorderLines(items ?? []))} className="btn btn-outline h-8 min-h-8 gap-1.5 bg-white px-3 text-xs">
            <ShoppingCart className="h-3.5 w-3.5" /> Order them
          </button>
        </div>
      )}
      <ClientRegister
        title="Stock"
        description="Open an item to issue or add stock, see its history, or edit it."
        actions={
          <>
            <button type="button" onClick={onOpenCategories} className="btn btn-outline h-8 min-h-8 gap-1.5 px-3 text-xs">
              <Tags className="h-3.5 w-3.5" /> Categories
            </button>
            <button type="button" onClick={() => setEditing("new")} className="btn-primary h-8 min-h-8 px-3 text-xs">
              <PackagePlus className="mr-1.5 h-3.5 w-3.5" /> Add an item
            </button>
          </>
        }
        rows={items}
        config={itemConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          adjust.reset();
          toggle.reset();
          setQuantity("");
          setDirection("out");
          setViewing(row.id);
        }}
        openLabel={() => "Issue / add"}
        noun={["item", "items"]}
        exportName={{ file: "stock", title: "Stock on Hand" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={categories.length ? "No stock items yet. Add the first with Add an item." : "Start with Categories (e.g. Stationery), then add stock items."}
      />

      {item && !editing && (
        <DetailsModal
          size="3xl"
          title={`${item.name} · ${item.sku}`}
          headline={{ label: "In stock", value: `${item.current_stock} ${item.unit_of_measure}`, note: `Worth ${money(stockValue(item))} at ${money(item.unit_cost)} each` }}
          status={stockStatus(item)}
          facts={[
            ["Category", item.category_name],
            ["Kind", labelFrom(ITEM_TYPES, item.item_type)],
            ["Kept at", item.storage_location ?? undefined],
            ["Reorder at", String(item.reorder_level)],
            ["Minimum", String(item.minimum_stock)],
            ["Maximum", String(item.maximum_stock)],
            ["Barcode", item.barcode ?? undefined],
          ]}
          sections={[{ title: "Description", body: item.description }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" disabled={toggle.isPending} onClick={() => toggle.mutate(item)} className="mr-auto btn btn-outline">
                {item.is_active ? "Stop stocking it" : "Stock it again"}
              </button>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {item.is_active && needsReorder(item) && (
                <button type="button" className="btn btn-outline gap-1.5" onClick={() => onReorder(reorderLines([item]))}>
                  <ShoppingCart className="h-4 w-4" /> Order more
                </button>
              )}
              <button type="button" className="btn btn-primary gap-1.5" onClick={() => setEditing(item)}>
                <Pencil className="h-4 w-4" /> Edit
              </button>
            </>
          }
        >
          {toggle.isError && errorLine(toggle.error, "The item could not be changed.")}
          {item.is_active && (
            <ReasonPanel
              key={item.current_stock}
              title="Issue or add stock"
              description="For stock issued to classes or offices, found in a count, or written off. Deliveries on an order are received on the order."
              label="What for"
              placeholder={direction === "out" ? "e.g. Issued to Primary 4 for the term." : "e.g. Found 10 more in the stock count."}
              context={{ key: "inventory.note", params: { kind: "adjustment", id: item.id, quantity: change || undefined } }}
              button={direction === "out" ? "Take out of stock" : "Add to stock"}
              busy={adjust.isPending}
              error={adjust.error}
              errorText="The count could not be changed."
              onSubmit={(reason) => change && after >= 0 && adjust.mutate({ id: item.id, change, reason })}
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Direction" as="div">
                  <div className="segmented">
                    <button type="button" aria-pressed={direction === "out"} onClick={() => setDirection("out")} className="segmented-option">Issued or written off</button>
                    <button type="button" aria-pressed={direction === "in"} onClick={() => setDirection("in")} className="segmented-option">Added</button>
                  </div>
                </Field>
                <Field
                  label={`How many (${item.unit_of_measure})`}
                  hint={change ? <span className={after < 0 ? "text-red-700" : undefined}>{item.current_stock} → {after}{after < 0 ? ": more than there is" : ""}</span> : undefined}
                >
                  <input type="number" min={1} step={1} value={quantity} onChange={(event) => setQuantity(event.target.value)} className="input mt-1 tabular-nums" />
                </Field>
              </div>
            </ReasonPanel>
          )}

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">History</h4>
            {history.isLoading ? (
              <div className="mt-1.5 h-16 animate-pulse rounded bg-gray-100" />
            ) : !history.data?.length ? (
              <p className="mt-1.5 text-sm text-gray-500">No changes recorded.</p>
            ) : (
              <div className="sheet-wrap mt-1.5 max-h-72 overflow-y-auto">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>What</th>
                      <th className="text-right">Change</th>
                      <th className="text-right">After</th>
                      <th>Why / reference</th>
                      <th>By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.data.map((row) => (
                      <tr key={row.id}>
                        <td className="whitespace-nowrap tabular-nums">{day(row.created_at)}</td>
                        <td>{movementLabel(row)}</td>
                        <td className={`text-right tabular-nums ${row.quantity < 0 ? "text-red-700" : "text-emerald-700"}`}>{row.quantity > 0 ? `+${row.quantity}` : row.quantity}</td>
                        <td className="text-right tabular-nums">{row.quantity_after}</td>
                        <td className="min-w-[12rem] whitespace-normal text-gray-700">{[row.reason, row.reference_number].filter(Boolean).join(" · ") || "—"}</td>
                        <td className="text-gray-700">{row.performed_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </DetailsModal>
      )}

      {editing && (
        <ItemForm
          item={editing === "new" ? undefined : editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onDone={(saved) => {
            const created = editing === "new";
            setEditing(null);
            setNotice({ tone: "success", text: created ? `${saved.name} added with ${saved.current_stock} ${saved.unit_of_measure} in stock.` : `${saved.name} updated.` });
            setViewing(saved.id);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
